// dev/app-skeleton/src/lib/draft.ts
// 草稿加密落 IDB（PRD v1.1 决策 ②）。
// draft_key = HKDF(root_key, "draft|v1|" + entry_id)
// AAD = buildAAD({ kind:"draft", user_id, resource_id:entry_id })
// 正式保存为 entry 后清除对应 draft。
//
// D15 Task 2：autosaver 改 debounce（停打字 5s 才落 IDB，而不是首次入队后 5s），
//   并暴露 onStateChange(cb) 让 UI 显示"正在自动保存…" / "已自动保存 hh:mm:ss"。
import { aeadOpen, aeadSeal, buildAAD, zeroize } from "../crypto";
import { db } from "./db";
import { deriveFromRoot } from "./session-key";
export async function saveDraft(userId, entryId, draft) {
    const key = deriveFromRoot("draft", entryId);
    try {
        const aad = buildAAD({ kind: "draft", user_id: userId, resource_id: entryId });
        const pt = new TextEncoder().encode(JSON.stringify(draft));
        const { bytes } = aeadSeal(key, pt, aad);
        await db.drafts.put({ entry_id: entryId, encrypted_blob: bytes, updated_at: Date.now() });
    }
    finally {
        zeroize(key);
    }
}
export async function loadDraft(userId, entryId) {
    const row = await db.drafts.get(entryId);
    if (!row)
        return null;
    const key = deriveFromRoot("draft", entryId);
    try {
        const aad = buildAAD({ kind: "draft", user_id: userId, resource_id: entryId });
        const pt = aeadOpen(key, row.encrypted_blob, aad);
        return JSON.parse(new TextDecoder().decode(pt));
    }
    catch {
        // 损坏或换 user_id → 删
        await db.drafts.delete(entryId);
        return null;
    }
    finally {
        zeroize(key);
    }
}
export async function clearDraft(entryId) {
    await db.drafts.delete(entryId);
}
/** 5s 自动草稿 hook —— debounce 版本。
 *  - 每次 queue() 都会清掉前一个 timer 并重置 intervalMs；
 *  - 用户停止打字 intervalMs 之后才真正落 IDB；
 *  - flush() 立即落盘；commit() 落盘后清 draft；
 *  - onStateChange/getState 给 UI 渲染"正在自动保存…/已自动保存 hh:mm:ss"。 */
export function makeDraftAutosaver(userId, entryId, intervalMs = 5000) {
    let timer = null;
    let pending = null;
    let state = { kind: "idle" };
    const subs = new Set();
    function setState(next) {
        state = next;
        for (const cb of subs) {
            try {
                cb(state);
            }
            catch { /* swallow */ }
        }
    }
    async function doSave() {
        if (!pending)
            return;
        const snap = pending;
        pending = null;
        setState({ kind: "saving" });
        try {
            await saveDraft(userId, entryId, snap);
            setState({ kind: "saved", savedAt: Date.now() });
        }
        catch (e) {
            setState({ kind: "error", message: e instanceof Error ? e.message : "草稿保存失败" });
        }
    }
    return {
        queue(d) {
            pending = d;
            if (timer)
                clearTimeout(timer); // debounce：重置计时器
            setState({ kind: "dirty" });
            timer = setTimeout(() => {
                timer = null;
                void doSave();
            }, intervalMs);
        },
        async flush() {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            if (pending)
                await doSave();
        },
        async commit() {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            pending = null;
            await clearDraft(entryId);
            setState({ kind: "idle" });
        },
        onStateChange(cb) {
            subs.add(cb);
            return () => { subs.delete(cb); };
        },
        getState() { return state; },
    };
}
